"use client";

import * as React from "react";

/**
 * Subscribes to a media query. Used by the shell to pick its layout, and by the
 * sheet to pick its presentation.
 *
 * `useSyncExternalStore` rather than state-plus-effect, because this ships to a
 * Next.js App Router consumer as well as a Vite SPA. There, a client component
 * is still rendered on the server, and `window.matchMedia` in a state
 * initialiser is a `ReferenceError` that fails the build. The server snapshot
 * answers `false` — below the breakpoint — so the server renders the narrow
 * layout and the client corrects it on hydration. In a plain SPA render there is
 * no server snapshot and the answer is right the first time.
 */
export function useMediaQuery(query: string) {
  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      const mq = window.matchMedia?.(query);
      if (!mq) return () => {};
      mq.addEventListener("change", onStoreChange);
      return () => mq.removeEventListener("change", onStoreChange);
    },
    [query],
  );

  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia?.(query).matches ?? false,
    () => false,
  );
}
